
import { formatFieldSample } from "@/modules/Etiquetas/services/etiquetaDefinition";

const ruleOption = {
  quando_preco: "preco",
  quando_codigo_barras: "cod_barras",
  quando_referencia: "considerar_referencia",
  quando_codigo_interno: "considerar_codigo_interno",
  quando_valor_kg: "valor_por_kg",
  quando_logo: "imprimir_logo",
};

const MAIN_AND_TECHNICAL_FIELD_KEYS = new Set([
  "produto.descricao",
  "produto.descricao_complementar",
  "produto.codigo_barras_etiqueta",
  "produto.codigo_legenda_etiqueta",
  "produto.codigo_barras_a4054",
  "produto.referencia_preco",
  "produto.preco",
  "produto.preco_kg",
  "impressao.texto_personalizado",
]);

const fallbackFields = {
  "produto.descricao": {
    key: "produto.descricao",
    label: "Descrição do produto",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.descricao_complementar": {
    key: "produto.descricao_complementar",
    label: "Descrição complementar",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.codigo_barras_etiqueta": {
    key: "produto.codigo_barras_etiqueta",
    label: "Código escolhido para a etiqueta",
    format: "texto",
    display_rule: "se_preenchido",
  },
  "produto.preco": {
    key: "produto.preco",
    label: "Preço de venda",
    format: "moeda",
    display_rule: "quando_preco",
  },
  "produto.preco_kg": {
    key: "produto.preco_kg",
    label: "Preço por KG",
    format: "moeda",
    display_rule: "quando_valor_kg",
  },
  "impressao.texto_personalizado": {
    key: "impressao.texto_personalizado",
    label: "Texto informado na impressão",
    format: "texto",
    display_rule: "se_preenchido",
  },
};

export default {
  name: "EtiquetaFieldPalette",
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    printOptions: {
      type: Object,
      default: () => ({}),
    },
    customText: {
      type: String,
      default: "",
    },
    elements: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    convertedLegacy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      supportOpen: false,
      fixedOpen: false,
      fixedSearch: "",
    };
  },
  computed: {
    orderedElements() {
      return [...this.elements].sort(
        (first, second) => Number(first.ordem || 0) - Number(second.ordem || 0)
      );
    },
    activeCodeLabels() {
      const labels = [];
      if (this.printOptions.considerar_referencia) labels.push("referência");
      if (this.printOptions.cod_barras) labels.push("EAN");
      if (this.printOptions.considerar_codigo_interno) {
        labels.push(
          this.printOptions.codigo_interno_somente_numeracao
            ? "código interno em número"
            : "código interno"
        );
      }
      return labels;
    },
    availablePresets() {
      const presets = [
        this.fieldPreset(
          "descricao",
          "Descrição do produto",
          "produto.descricao",
          "fas fa-align-left",
          "Informação principal do produto."
        ),
        this.fieldPreset(
          "descricao_complementar",
          "Descrição complementar",
          "produto.descricao_complementar",
          "fas fa-align-left",
          "Detalhes adicionais do produto."
        ),
      ];

      if (this.activeCodeLabels.length) {
        presets.push({
          key: "codigo_dinamico",
          label: "Código para impressão",
          helper: `Será impresso: ${this.activeCodeLabels.join(" + ")}.`,
          icon: "fas fa-barcode",
          type: "barcode",
          field: this.field("produto.codigo_barras_etiqueta"),
          match: (element) => this.isScenarioBarcode(element),
        });
      }
      if (this.printOptions.preco) {
        presets.push(
          this.fieldPreset(
            "preco",
            "Preço de venda",
            "produto.preco",
            "fas fa-dollar-sign",
            'Será impresso quando "Preço" estiver ligado.',
            ["produto.preco", "produto.referencia_preco"]
          )
        );
      }
      if (this.printOptions.valor_por_kg) {
        presets.push(
          this.fieldPreset(
            "preco_kg",
            "Preço por KG",
            "produto.preco_kg",
            "fas fa-weight-hanging",
            'Será impresso quando "Valor por KG" estiver ligado.'
          )
        );
      }
      if (this.printOptions.imprimir_logo) {
        presets.push({
          key: "logo",
          label: "Logo da empresa",
          helper: 'Será impressa quando "Logo" estiver ligada.',
          icon: "far fa-image",
          type: "logo",
          field: null,
          match: (element) => element.tipo === "logo",
        });
      }

      presets.push({
        ...this.fieldPreset(
          "texto_impressao",
          "Texto informado na impressão",
          "impressao.texto_personalizado",
          "fas fa-comment-alt",
          this.customText.trim()
            ? `Conteúdo do teste: “${this.customText.trim()}”`
            : "Preencha o texto personalizado na aba Exemplo."
        ),
        disabled: !this.customText.trim(),
      });

      return presets;
    },
    fixedFields() {
      const search = this.normalize(this.fixedSearch);
      return this.fields.filter((field) => {
        if (MAIN_AND_TECHNICAL_FIELD_KEYS.has(field.key)) return false;
        if (!search) return true;
        return this.normalize(
          `${field.label || ""} ${field.category || ""} ${field.key || ""}`
        ).includes(search);
      });
    },
    groupedFixedFields() {
      const groups = this.fixedFields.reduce((result, field) => {
        const category = field.category || "Outras informações";
        if (!result[category]) result[category] = [];
        result[category].push(field);
        return result;
      }, {});
      return Object.keys(groups).map((category) => ({
        category,
        items: groups[category],
      }));
    },
  },
  methods: {
    field(key) {
      return (
        this.fields.find((candidate) => candidate.key === key) ||
        fallbackFields[key]
      );
    },
    fieldPreset(key, label, fieldKey, icon, helper, matchKeys = [fieldKey]) {
      return {
        key,
        label,
        helper,
        icon,
        type: "field",
        field: this.field(fieldKey),
        match: (element) =>
          element.tipo === "field" && matchKeys.includes(element.campo),
      };
    },
    existingElement(preset) {
      return this.elements.find((element) => preset.match(element)) || null;
    },
    usePreset(preset) {
      const existing = this.existingElement(preset);
      if (existing) {
        this.$emit("select", existing.id);
        return;
      }
      if (!preset.disabled) this.$emit("add", preset.type, preset.field);
    },
    elementLabel(element) {
      return element.nome || "Elemento sem nome";
    },
    elementKind(element) {
      if (element.tipo === "field") {
        return this.field(element.campo)?.label || element.campo || "Campo";
      }
      return (
        {
          text: "Texto livre",
          barcode: this.isScenarioBarcode(element)
            ? "Código conforme as opções do teste"
            : "Código de barras fixo do modelo",
          qrcode: "QR Code",
          logo: "Logo da empresa",
          line: "Linha de apoio",
          rectangle: "Caixa / borda",
        }[element.tipo] || "Elemento visual"
      );
    },
    elementIcon(element) {
      return (
        {
          text: "fas fa-font",
          barcode: "fas fa-barcode",
          qrcode: "fas fa-qrcode",
          logo: "far fa-image",
          line: "fas fa-minus",
          rectangle: "far fa-square",
        }[element.tipo] || "fas fa-align-left"
      );
    },
    elementStatus(element) {
      if (element.visivel === false) {
        return { label: "Oculto", className: "is-off" };
      }
      const option = ruleOption[element.exibir_quando];
      if (option && !this.printOptions[option]) {
        return { label: "Opção desligada", className: "is-off" };
      }
      if (this.isScenarioBarcode(element) && !this.activeCodeLabels.length) {
        return { label: "Sem código no teste", className: "is-off" };
      }
      return { label: "Configurado", className: "is-on" };
    },
    isScenarioBarcode(element) {
      if (element?.tipo !== "barcode") return false;
      if (
        [
          "produto.codigo_barras_etiqueta",
          "produto.codigo_barras_a4054",
        ].includes(element.campo)
      ) {
        return true;
      }
      return (
        element.campo === "produto.codigo_barras" &&
        (this.convertedLegacy ||
          element.exibir_quando === "quando_codigo_barras")
      );
    },
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    existingFixedElement(field) {
      return (
        this.elements.find(
          (element) =>
            element.tipo === "field" &&
            element.campo === field.key &&
            ["se_preenchido", "sempre"].includes(
              element.exibir_quando || "se_preenchido"
            )
        ) || null
      );
    },
    useFixedField(field) {
      const existing = this.existingFixedElement(field);
      if (existing) {
        this.$emit("select", existing.id);
        return;
      }
      this.$emit("add", "field", {
        ...field,
        display_rule: "se_preenchido",
      });
    },
    fixedFieldSample(field) {
      return formatFieldSample(field);
    },
    fixedFieldIcon(field) {
      return (
        {
          moeda: "fas fa-dollar-sign",
          data: "far fa-calendar-alt",
          numero: "fas fa-hashtag",
        }[field.format] || "fas fa-font"
      );
    },
  },
};
